import { useCallback, useMemo, useState } from 'react'
import { routes } from '@/routes'
import { ReservationWidget as ReservationWidgetConst } from '@sevenrooms/domain/constants'
import type { LanguageCode } from '@sevenrooms/locales'
import { useNavigation } from '@sevenrooms/navigation'
import type { SelectOption } from '@sevenrooms/ui-kit/core'
import { useVenue } from './useVenue'

export function useWidgetLanguage() {
  const { matchQuery, updateQuery } = useNavigation()
  const { lang: urlLang } = matchQuery(routes.reservations) ?? {}
  const { venueLanguages } = useVenue()
  const defaultLanguage = useMemo(() => venueLanguages.find(lang => lang.isDefault), [venueLanguages])
  const preferredLanguageLocalStorage = useMemo(() => {
    const prefLang = window.localStorage.getItem(ReservationWidgetConst.PreferredLanguageCookie)
    if (prefLang) {
      return JSON.parse(prefLang) as LanguageCode
    }
    return undefined
  }, [])
  const selectedLanguage: LanguageCode = useMemo(() => {
    if (venueLanguages.find(lang => lang.value === urlLang && lang.isEnabled)) {
      return urlLang as LanguageCode
    }
    return preferredLanguageLocalStorage || (defaultLanguage?.value ?? 'en')
  }, [defaultLanguage?.value, preferredLanguageLocalStorage, urlLang, venueLanguages])
  const [previouslySelectedLanguage, setPreviouslySelectedLanguage] = useState(selectedLanguage)
  const setSelectedLanguage = useCallback(
    (lang: LanguageCode) => {
      setPreviouslySelectedLanguage(selectedLanguage)
      updateQuery(
        routes.reservations,
        {
          lang,
        },
        'replaceIn'
      )
    },
    [selectedLanguage, updateQuery]
  )
  const availableLanguageOptions: SelectOption<LanguageCode>[] = useMemo(
    () =>
      venueLanguages
        .filter(lang => lang.isEnabled)
        .map(lang => ({
          id: lang.value,
          label: lang.name,
        })),
    [venueLanguages]
  )
  return {
    defaultLanguage,
    selectedLanguage,
    previouslySelectedLanguage,
    setSelectedLanguage,
    availableLanguageOptions,
  }
}
